import { useState, useRef } from "react";
import {
  Card, Typography, Button, Modal, Progress, Space, Upload, message,
} from "antd";
import {
  UploadOutlined, CheckCircleFilled, SyncOutlined,
} from "@ant-design/icons";
import { subirExcelAprobados } from "../../services/reporteKokam/temporalKokamAprobadosService";

const { Title, Text } = Typography;

const MENSAJES_PROGRESO = [
  "Leyendo archivo Excel...",
  "Validando estructura de columnas...",
  "Procesando filas del reporte...",
  "Insertando registros en la base de datos...",
  "Guardando aprobados en lotes...",
  "Verificando integridad de los datos...",
  "Finalizando carga...",
];

const SubirExcelAprobados = () => {
  const [modalVisible,  setModalVisible]  = useState(false);
  const [progreso,      setProgreso]      = useState(0);
  const [mensajeActual, setMensajeActual] = useState(MENSAJES_PROGRESO[0]);
  const [finalizado,    setFinalizado]    = useState(false);
  const [resultado,     setResultado]     = useState(null);
  const [cargando,      setCargando]      = useState(false);

  const intervalRef   = useRef(null);
  const mensajeIdxRef = useRef(0);

  // ── Progreso animado ─────────────────────────────
  const iniciarProgreso = () => {
    setProgreso(0);
    setFinalizado(false);
    setResultado(null);
    setMensajeActual(MENSAJES_PROGRESO[0]);
    mensajeIdxRef.current = 0;
    setModalVisible(true);

    intervalRef.current = setInterval(() => {
      setProgreso((prev) => {
        if (prev >= 85) {
          clearInterval(intervalRef.current);
          return prev;
        }

        const siguiente = prev + Math.random() * 3 + 0.5;

        const nuevoIdx = Math.min(
          Math.floor(siguiente / (85 / MENSAJES_PROGRESO.length)),
          MENSAJES_PROGRESO.length - 1
        );

        if (nuevoIdx !== mensajeIdxRef.current) {
          mensajeIdxRef.current = nuevoIdx;
          setMensajeActual(MENSAJES_PROGRESO[nuevoIdx]);
        }

        return Math.min(siguiente, 85);
      });
    }, 500);
  };

  const finalizarProgreso = (data) => {
    clearInterval(intervalRef.current);
    setMensajeActual("¡Carga completada!");
    setResultado(data);

    let p = 85;
    const finish = setInterval(() => {
      p += 3;
      setProgreso(Math.min(p, 100));

      if (p >= 100) {
        clearInterval(finish);
        setFinalizado(true);
      }
    }, 60);
  };

  const cerrarModal = () => {
    setModalVisible(false);
    setProgreso(0);
    setFinalizado(false);
    setResultado(null);
  };

  // ── Upload handler ─────────────────────────────
  const handleUpload = async (file) => {
    const tiposValidos = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!tiposValidos.includes(file.type)) {
      message.error("Solo se permiten archivos .xlsx o .xls");
      return false;
    }

    setCargando(true);
    iniciarProgreso();

    try {
      const data = await subirExcelAprobados(file);
      finalizarProgreso(data);
    } catch (error) {
      clearInterval(intervalRef.current);
      setModalVisible(false);

      message.error(
        error?.response?.data?.message || "Error al subir el archivo"
      );
    } finally {
      setCargando(false);
    }

    return false;
  };

  // ── Render ─────────────────────────────
  return (
    <Card style={{ margin: 20 }}>
      <Title level={3}>Carga de Aprobados</Title>

      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Sube el reporte Excel de aprobados del proyecto. Formatos aceptados: .xlsx, .xls
      </Text>

      <Upload
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={handleUpload}
        disabled={cargando}
      >
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={cargando}
          size="large"
        >
          {cargando ? "Procesando..." : "Seleccionar y subir Excel"}
        </Button>
      </Upload>

      {/* ── Modal ───────────────────────────── */}
      <Modal
        open={modalVisible}
        footer={
          finalizado
            ? <Button type="primary" onClick={cerrarModal}>Cerrar</Button>
            : null
        }
        closable={false}
        maskClosable={false}
        centered
        width={460}
        title={
          <Space>
            {finalizado
              ? <CheckCircleFilled style={{ color: "#52c41a" }} />
              : <SyncOutlined spin style={{ color: "#1677ff" }} />}
            <span>
              {finalizado ? "Carga completada" : "Cargando aprobados..."}
            </span>
          </Space>
        }
      >
        <div style={{ padding: "16px 0" }}>
          <Progress
            percent={Math.round(progreso)}
            status={finalizado ? "success" : "active"}
          />

          <div style={{ marginTop: 12 }}>
            {finalizado && resultado ? (
              <Space direction="vertical">
                <Text strong style={{ color: "#52c41a" }}>
                  Carga completada exitosamente
                </Text>

                <Text>
                  Total registros: <strong>{resultado.total ?? 0}</strong>
                </Text>

                <Text>
                  Insertados:{" "}
                  <strong style={{ color: "#1677ff" }}>
                    {resultado.insertados ?? 0}
                  </strong>
                </Text>
              </Space>
            ) : (
              <Text type="secondary">{mensajeActual}</Text>
            )}
          </div>
        </div>
      </Modal>
    </Card>
  );
};

export default SubirExcelAprobados;